/**
 * Full-text index over `word_definition.definition`.
 *
 * NOT a drizzle table: FTS5 is a virtual table, which drizzle's schema builder
 * cannot express, so the DDL lives here as raw SQL and is applied in two places
 * that must not drift — the static-DB build (`buildStaticDatabase`) and the D1
 * migration that mirrors the same schema. Both import these constants rather
 * than re-typing the statements.
 *
 * WHY it exists: the word search matches an English query against every
 * definition, and `LIKE '%fire%'` cannot use an index — it scans all ~281k
 * `word_definition` rows on every query (~85-155ms measured on the shipped
 * `static.db`). The FTS index takes the same lookup to ~0.3ms.
 *
 * It is also MORE correct than the scan it replaces. The old predicate
 * space-padded the definition and matched ` fire `, so a sense ending in
 * punctuation was invisible: 交火 ("to exchange fire; to engage in a firefight")
 * did not match "fire" because the text reads `fire;`. `unicode61` tokenizes on
 * character class, so those hits come back. Verified against the shipped DB: the
 * FTS result set is a strict SUPERSET of the old one for every probe query
 * (fire / water / good / train / one) — nothing that matched before stops
 * matching.
 *
 * `content=` makes this an EXTERNAL-CONTENT index: the tokens live here, the
 * text stays in `word_definition`, so the index costs ~8 MB rather than
 * duplicating the column. That also means it does NOT self-update — the build
 * populates it once from a finished table, and the static DB is read-only at
 * runtime, so there is nothing to keep in sync afterwards.
 */

/** Virtual-table name. Referenced by the search query's `MATCH` clause. */
export const WORD_DEFINITION_FTS_TABLE = "word_definition_fts";

/**
 * Creates the (empty) index. `content_rowid` is `word_definition.id`, so a hit's
 * `rowid` joins straight back to the source row.
 */
export const CREATE_WORD_DEFINITION_FTS_SQL = `CREATE VIRTUAL TABLE ${WORD_DEFINITION_FTS_TABLE} USING fts5(definition, content='word_definition', content_rowid='id', tokenize='unicode61')`;

/** Fills the index from the finished `word_definition` table. */
export const POPULATE_WORD_DEFINITION_FTS_SQL = `INSERT INTO ${WORD_DEFINITION_FTS_TABLE}(rowid, definition) SELECT id, definition FROM word_definition`;

/**
 * Both statements in apply order. The D1 migration and the static-DB build run
 * exactly this list, which is what keeps the two schemas identical.
 */
export const WORD_DEFINITION_FTS_STATEMENTS = [
  CREATE_WORD_DEFINITION_FTS_SQL,
  POPULATE_WORD_DEFINITION_FTS_SQL,
] as const;
